/**
 * Employee self-service — "My Documents".
 *
 * Lists assignments for the current user with a simple status filter,
 * and lets them acknowledge or sign. The heavier flows (e-signature
 * canvas, PDF rendering) are out of scope here — this is the minimal
 * transactional surface Sentinel's nag emails need to link into.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, FileText, PenLine, AlertCircle } from "lucide-react";
import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  acknowledgeAssignment,
  listMyDocuments,
  signAssignment,
  type AssignmentStatus,
  type DocumentAssignment,
} from "@/api/documentAssignments";

const FILTERS: { value: "all" | AssignmentStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "viewed", label: "Viewed" },
  { value: "signed", label: "Signed" },
  { value: "overdue", label: "Overdue" },
];

function statusBadge(
  a: DocumentAssignment,
): { label: string; variant: "default" | "info" | "warning" | "danger" | "success" } {
  if (a.is_overdue || a.status === "overdue")
    return { label: "Overdue", variant: "danger" };
  if (a.status === "signed") return { label: "Signed", variant: "success" };
  if (a.status === "waived") return { label: "Waived", variant: "default" };
  if (a.status === "expired") return { label: "Expired", variant: "danger" };
  if (a.status === "viewed") return { label: "Viewed", variant: "info" };
  return { label: "Pending", variant: "warning" };
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function MyDocumentsPage() {
  const { addToast } = useToast();
  const [rows, setRows] = useState<DocumentAssignment[]>([]);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["value"]>(
    "all",
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  // Differentiated states so an empty result doesn't read as a fetch
  // failure. ``loading`` covers the first fetch; ``loadError`` is only
  // set when the request itself blew up (network, 500, schema mismatch).
  // An empty array is a valid 200 response — handled by the inline
  // empty-state in the card body, not an error toast.
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = async () => {
    setLoadError(null);
    try {
      const data = await listMyDocuments();
      setRows(data);
    } catch (err) {
      // Surface the failure inline instead of the old transient toast,
      // which disappeared after a few seconds and left the page looking
      // exactly like the empty-state.
      const msg =
        err instanceof Error
          ? err.message
          : "We couldn't load your documents.";
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "overdue")
      return rows.filter(
        (r) => r.is_overdue || r.status === "overdue",
      );
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const counts = useMemo(
    () => ({
      pending: rows.filter(
        (r) => r.status === "pending" || r.status === "viewed",
      ).length,
      overdue: rows.filter((r) => r.is_overdue || r.status === "overdue")
        .length,
      signed: rows.filter((r) => r.status === "signed").length,
    }),
    [rows],
  );

  const acknowledge = async (id: string) => {
    setBusyId(id);
    try {
      await acknowledgeAssignment(id);
      addToast("success", "Acknowledged");
      await load();
    } catch {
      addToast("error", "Failed to acknowledge");
    } finally {
      setBusyId(null);
    }
  };

  const sign = async (id: string) => {
    setBusyId(id);
    try {
      await signAssignment(id);
      addToast("success", "Signed");
      await load();
    } catch {
      addToast("error", "Failed to sign");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="My Documents"
        subtitle="Everything you've been assigned to review or sign"
      />

      {/* Quick stats — single column on phones, 3-up on >=sm so the
          tile copy doesn't cram below 375px. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatTile
          label="Pending"
          value={counts.pending}
          icon={<FileText className="h-4 w-4" />}
        />
        <StatTile
          label="Overdue"
          value={counts.overdue}
          tone={counts.overdue > 0 ? "danger" : undefined}
          icon={<AlertCircle className="h-4 w-4" />}
        />
        <StatTile
          label="Completed"
          value={counts.signed}
          tone="success"
          icon={<CheckCircle2 className="h-4 w-4" />}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
          <div className="flex items-center gap-1 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  filter === f.value
                    ? "bg-accent text-white border-accent"
                    : "border-border text-text-muted hover:bg-surface-hover"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </CardHeader>

        {loadError ? (
          <div className="py-10 text-center text-sm">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-danger" />
            <p className="text-text-primary font-medium">
              We couldn&rsquo;t load your documents.
            </p>
            <p className="text-text-muted mt-1">{loadError}</p>
            <Button
              size="sm"
              variant="secondary"
              className="mt-3"
              onClick={() => {
                setLoading(true);
                load();
              }}
            >
              Try again
            </Button>
          </div>
        ) : loading ? (
          <div className="py-10 text-center text-sm text-text-muted">
            Loading your documents&hellip;
          </div>
        ) : visible.length === 0 ? (
          <div className="py-10 text-center text-sm text-text-muted">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
            {rows.length === 0 ? (
              <>
                <p className="text-text-primary">
                  Nothing assigned yet.
                </p>
                <p className="mt-1">
                  If you were expecting onboarding paperwork, ask your HR
                  admin to click <strong>Start Onboarding</strong> on
                  your profile — that&rsquo;s the step that fans out the
                  documents to you.
                </p>
              </>
            ) : (
              "Nothing in this filter."
            )}
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {visible.map((a) => {
              const b = statusBadge(a);
              const done = a.status === "signed" || a.status === "waived";
              const requiresSig = a.template?.requires_signature ?? false;
              return (
                <div
                  key={a.id}
                  className="py-3 flex items-center gap-3 justify-between"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/my/documents/${a.id}`}
                        className="font-medium text-text-primary truncate hover:text-accent hover:underline"
                      >
                        {a.template?.name ?? "Document"}
                      </Link>
                      <Badge variant={b.variant}>{b.label}</Badge>
                      {a.template?.category && (
                        <Badge variant="default">
                          {a.template.category}
                        </Badge>
                      )}
                      {requiresSig && (
                        <Badge variant="info">Signature</Badge>
                      )}
                    </div>
                    <p className="text-xs text-text-muted mt-0.5 truncate">
                      {a.template?.description ||
                        "Assigned " + formatDate(a.assigned_at)}
                      {a.deadline_at
                        ? ` · Due ${formatDate(a.deadline_at)}`
                        : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.template?.file_url && (
                      <a
                        href={a.template.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs px-2 py-1 rounded border border-border hover:bg-surface-hover"
                      >
                        View
                      </a>
                    )}
                    {!done && !requiresSig && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busyId === a.id}
                        onClick={() => acknowledge(a.id)}
                      >
                        Acknowledge
                      </Button>
                    )}
                    {!done && requiresSig && (
                      <Button
                        size="sm"
                        disabled={busyId === a.id}
                        onClick={() => sign(a.id)}
                      >
                        <PenLine className="h-4 w-4" />
                        Sign
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </motion.div>
  );
}

function StatTile({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: number;
  tone?: "success" | "danger";
  icon: React.ReactNode;
}) {
  const color =
    tone === "success"
      ? "text-success"
      : tone === "danger"
        ? "text-danger"
        : "text-text-primary";
  return (
    <div className="rounded-lg border border-border/40 p-3">
      <div className="flex items-center gap-2 text-xs text-text-muted">
        {icon}
        {label}
      </div>
      <div className={`text-2xl font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
